import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import { fetchLeaderboard, fetchSeries, fetchGroupBets } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { calculateClientLeaderboard } from '@/lib/scoring';
import { DrillInLayout } from '@/components/DrillInLayout';
import Leaderboard from '@/components/Leaderboard';
import { Loader2 } from 'lucide-react';

const LeaderboardDrillIn = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupId || !isMember) return;
    const load = async () => {
      const [{ data: g }, s, lb, groupBets, { data: members }] = await Promise.all([
        supabase.from('groups').select('name').eq('id', groupId).single(),
        fetchSeries(),
        fetchLeaderboard(groupId),
        fetchGroupBets(groupId),
        supabase.from('group_members').select('user_id, display_name').eq('group_id', groupId),
      ]);
      setGroupName(g?.name || '');
      setSeriesList(s);
      if (lb && lb.length > 0) {
        setLeaderboard(lb);
      } else {
        const memberNames: Record<string, string> = {};
        members?.forEach((m: any) => { if (m.user_id) memberNames[m.user_id] = m.display_name; });
        setLeaderboard(calculateClientLeaderboard(groupBets, s, memberNames));
      }
      setLoading(false);
    };
    load();
  }, [groupId, isMember]);

  if (!membershipLoading && isMember === false) return <Navigate to="/" replace />;

  const completedSeriesCount = seriesList.filter(s => s.status === 'completed').length;

  return (
    <DrillInLayout title="טבלת דירוג" groupId={groupId!}>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      ) : (
        <Leaderboard
          entries={leaderboard}
          currentUserId={user?.id}
          totalSeries={completedSeriesCount || seriesList.length}
          groupName={groupName}
          groupId={groupId!}
        />
      )}
    </DrillInLayout>
  );
};

export default LeaderboardDrillIn;
